/**
 * Achievements — milestone badges derived purely from the stats Progress already
 * tracks. Definitions live here (titles/descriptions are English flavor, not
 * routed through i18n to keep the bundles lean); unlock state lives in Progress.
 *
 * `checkAndUnlock()` is the single post-level checkpoint: it evaluates every
 * predicate against a fresh snapshot, persists any newly-cleared ones, and
 * returns them so the UI can pop a toast.
 */
import { Progress } from './progress';

type Snapshot = ReturnType<typeof Progress.achievementSnapshot>;

export interface AchievementDef {
  id: string;
  title: string;
  desc: string;
  test: (s: Snapshot) => boolean;
}

export const ACHIEVEMENTS: AchievementDef[] = [
  { id: 'first_breach', title: 'First Contact',     desc: 'Breach your first node.',          test: s => s.wins >= 1 },
  { id: 'ten',          title: 'Getting Loud',      desc: 'Clear 10 levels.',                 test: s => s.wins >= 10 },
  { id: 'fifty',        title: 'System Admin',      desc: 'Clear 50 levels.',                 test: s => s.wins >= 50 },
  { id: 'hundred',      title: 'Root Access',       desc: 'Clear 100 levels.',                test: s => s.wins >= 100 },
  { id: 'endless10',    title: 'Down the Stack',    desc: 'Reach endless level 10.',          test: s => s.endlessBest >= 9 },
  { id: 'endless50',    title: 'Deep Run',          desc: 'Reach endless level 50.',          test: s => s.endlessBest >= 49 },
  { id: 'boss',         title: 'Giant Slayer',      desc: 'Take down a boss node.',           test: s => s.endlessBest >= 19 },
  { id: 'endless100',   title: 'Abyssal',           desc: 'Reach endless level 100.',         test: s => s.endlessBest >= 99 },
  { id: 'mega',         title: 'Kaiju Killer',      desc: 'Crash a megaboss.',                test: s => s.endlessBest >= 99 },
  { id: 'streak5',      title: 'On a Roll',         desc: 'Hit a 5-win streak.',              test: s => s.endlessBestStreak >= 5 },
  { id: 'streak10',     title: 'Unstoppable',       desc: 'Hit a 10-win streak.',             test: s => s.endlessBestStreak >= 10 },
  { id: 'streak25',     title: 'Flawless Cascade',  desc: 'Hit a 25-win streak.',             test: s => s.endlessBestStreak >= 25 },
  { id: 'daily1',       title: 'Daily Driver',      desc: 'Solve a daily challenge.',         test: s => s.dailySolvedCount >= 1 },
  { id: 'daily7',       title: 'Week Streak',       desc: 'Keep a 7-day daily streak.',       test: s => s.dailyBestStreak >= 7 },
  { id: 'daily30',      title: 'Monthly Ritual',    desc: 'Keep a 30-day daily streak.',      test: s => s.dailyBestStreak >= 30 },
  { id: 'hint',         title: 'Big Brain',         desc: 'Use a hint to rule one out.',      test: s => s.hintsUsed >= 1 },
  { id: 'comeback',     title: 'Second Wind',       desc: 'Watch an ad for an extra life.',   test: s => s.livesFromAds >= 1 },
  { id: 'score5k',      title: 'High Scorer',       desc: 'Bank 5,000 points in a run.',      test: s => s.bestScore >= 5000 },
  { id: 'score25k',     title: 'Score Demon',       desc: 'Bank 25,000 points in a run.',     test: s => s.bestScore >= 25000 },
];

/** Evaluate all achievements, persist + return any newly unlocked. */
export function checkAndUnlock(): AchievementDef[] {
  const snap = Progress.achievementSnapshot();
  const fresh: AchievementDef[] = [];
  for (const a of ACHIEVEMENTS) {
    if (Progress.isAchievementUnlocked(a.id)) continue;
    if (a.test(snap)) { Progress.unlockAchievement(a.id); fresh.push(a); }
  }
  return fresh;
}

/** All achievements paired with their current unlocked state (for the screen). */
export function achievementList(): { def: AchievementDef; unlocked: boolean }[] {
  return ACHIEVEMENTS.map(def => ({ def, unlocked: Progress.isAchievementUnlocked(def.id) }));
}

export function unlockedCount(): number {
  return ACHIEVEMENTS.reduce((n, a) => n + (Progress.isAchievementUnlocked(a.id) ? 1 : 0), 0);
}
